import isEmpty from 'lodash/isEmpty';
import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';

import { AffiliationLevel, GUID, InternalUserProfile } from '@ndvr/auth-api';

import { AFFILIATION_LEVEL_EXTERNAL_VALUE } from './HouseholdFiltersHook';

export enum HouseholdFilterKeys {
    primaryAdvisorGUIDs = 'primaryAdvisorGUIDs',
    affiliationLevels = 'affiliationLevels',
    hasMoneyInvested = 'hasMoneyInvested',
    isHNW = 'isHNW',
    minAUM = 'minAUM',
    maxAUM = 'maxAUM',
    createdAtStart = 'createdAtStart',
    createdAtEnd = 'createdAtEnd',
    agreementSignedStart = 'agreementSignedStart',
    agreementSignedEnd = 'agreementSignedEnd',
}

export type AffiliationFilter = AffiliationLevel | typeof AFFILIATION_LEVEL_EXTERNAL_VALUE;

type HouseholdFilterKeysFields = { [key in HouseholdFilterKeys]: unknown };
export interface HouseholdFilters extends HouseholdFilterKeysFields {
    primaryAdvisorGUIDs: GUID<InternalUserProfile>[];
    affiliationLevels: AffiliationFilter[];
    hasMoneyInvested: boolean;
    isHNW: boolean;
    minAUM: number | null;
    maxAUM: number | null;
    createdAtStart: string | null;
    createdAtEnd: string | null;
    agreementSignedStart: string | null;
    agreementSignedEnd: string | null;
}

const HOUSEHOLD_FILTERS_DEFAULT: HouseholdFilters = {
    [HouseholdFilterKeys.primaryAdvisorGUIDs]: [],
    [HouseholdFilterKeys.affiliationLevels]: [],
    [HouseholdFilterKeys.hasMoneyInvested]: false,
    [HouseholdFilterKeys.isHNW]: false,
    [HouseholdFilterKeys.minAUM]: null,
    [HouseholdFilterKeys.maxAUM]: null,
    [HouseholdFilterKeys.createdAtStart]: null,
    [HouseholdFilterKeys.createdAtEnd]: null,
    [HouseholdFilterKeys.agreementSignedStart]: null,
    [HouseholdFilterKeys.agreementSignedEnd]: null,
};

export interface HouseholdFiltersCtx {
    filters: HouseholdFilters;
    patchFilters: (partialFilters: Partial<HouseholdFilters>) => void;
    resetFilters: (householdFilterKeys?: HouseholdFilterKeys[]) => void;
    isFilterActive: boolean;
    isAdvisorFilterActive: boolean;
    isAUMRangeFilterActive: boolean;
    isAffiliationLevelFilterActive: boolean;
    isCreatedAtFilterActive: boolean;
    isAgreementSignedFilterActive: boolean;
    isDatesFilterActive: boolean;
    primaryAdvisorFilterCount: number;
    affiliationLevelFilterCount: number;
    aumFilterCount: number;
    datesFilterCount: number;
}

const HouseholdFiltersContext = createContext<HouseholdFiltersCtx>({
    filters: HOUSEHOLD_FILTERS_DEFAULT,
    patchFilters: () => {},
    resetFilters: () => {},
    isFilterActive: false,
    isAdvisorFilterActive: false,
    isAUMRangeFilterActive: false,
    isAffiliationLevelFilterActive: false,
    isCreatedAtFilterActive: false,
    isAgreementSignedFilterActive: false,
    isDatesFilterActive: false,
    primaryAdvisorFilterCount: 0,
    affiliationLevelFilterCount: 0,
    aumFilterCount: 0,
    datesFilterCount: 0,
});

const FILTERS_LOCAL_STORAGE_KEY = 'household_list_active_filters';

export const HouseholdFiltersContextProvider = ({ children }: PropsWithChildren) => {
    const [filters, setFilters] = useState<HouseholdFilters>(HOUSEHOLD_FILTERS_DEFAULT);

    useEffect(() => {
        const filterStringified = globalThis.localStorage.getItem(FILTERS_LOCAL_STORAGE_KEY);
        if (filterStringified) {
            const localStorageFilters = JSON.parse(filterStringified);
            setFilters((filtersState) => ({ ...filtersState, ...localStorageFilters }));
        }
    }, []);

    useEffect(() => {
        globalThis.localStorage.setItem(FILTERS_LOCAL_STORAGE_KEY, JSON.stringify(filters));
    }, [filters]);

    const patchFilters = (partialFilters: Partial<HouseholdFilters>) =>
        setFilters((filtersState) => ({ ...filtersState, ...partialFilters }));

    const resetFilters = (filterKeysToReset: HouseholdFilterKeys[] = Object.values(HouseholdFilterKeys)) => {
        const filterDefaults: Partial<HouseholdFilters> = filterKeysToReset.reduce(
            (acc, filterKey: HouseholdFilterKeys) => ({
                ...acc,
                [filterKey]: HOUSEHOLD_FILTERS_DEFAULT[filterKey],
            }),
            {},
        );
        setFilters((filterState) => ({ ...filterState, ...filterDefaults }));
    };

    const isAdvisorFilterActive = useMemo(() => !isEmpty(filters.primaryAdvisorGUIDs), [filters.primaryAdvisorGUIDs]);

    const isAUMRangeFilterActive = useMemo(() => filters.hasMoneyInvested, [filters.hasMoneyInvested]);

    const isAffiliationLevelFilterActive = useMemo(
        () => !isEmpty(filters.affiliationLevels),
        [filters.affiliationLevels],
    );

    const isCreatedAtFilterActive = useMemo(
        () => !isEmpty(filters.createdAtStart) || !isEmpty(filters.createdAtEnd),
        [filters.createdAtStart, filters.createdAtEnd],
    );
    const isAgreementSignedFilterActive = useMemo(
        () => !isEmpty(filters.agreementSignedStart) || !isEmpty(filters.agreementSignedEnd),
        [filters.agreementSignedStart, filters.agreementSignedEnd],
    );
    const isDatesFilterActive = useMemo(
        () => isCreatedAtFilterActive || isAgreementSignedFilterActive,
        [isCreatedAtFilterActive, isAgreementSignedFilterActive],
    );

    const isFilterActive = useMemo(
        () => isAdvisorFilterActive || isAUMRangeFilterActive || isAffiliationLevelFilterActive || isDatesFilterActive,
        [isAdvisorFilterActive, isAUMRangeFilterActive, isAffiliationLevelFilterActive, isDatesFilterActive],
    );

    const primaryAdvisorFilterCount = useMemo(() => filters.primaryAdvisorGUIDs.length, [filters.primaryAdvisorGUIDs]);
    const affiliationLevelFilterCount = useMemo(() => filters.affiliationLevels.length, [filters.affiliationLevels]);
    const aumFilterCount = useMemo(() => [filters.hasMoneyInvested].filter(Boolean).length, [filters.hasMoneyInvested]);
    const datesFilterCount = useMemo(
        () => [isCreatedAtFilterActive, isAgreementSignedFilterActive].filter(Boolean).length,
        [isCreatedAtFilterActive, isAgreementSignedFilterActive],
    );

    return (
        <HouseholdFiltersContext.Provider
            value={{
                filters,
                patchFilters,
                resetFilters,
                isFilterActive,
                isAdvisorFilterActive,
                isAUMRangeFilterActive,
                isAffiliationLevelFilterActive,
                isCreatedAtFilterActive,
                isAgreementSignedFilterActive,
                isDatesFilterActive,
                primaryAdvisorFilterCount,
                affiliationLevelFilterCount,
                aumFilterCount,
                datesFilterCount,
            }}
        >
            {children}
        </HouseholdFiltersContext.Provider>
    );
};

export const useHouseholdFiltersContext = () => {
    const context = useContext(HouseholdFiltersContext);
    if (!context) {
        throw Error('useHouseholdFiltersContext must be used within HouseholdFiltersContextProvider');
    }
    return context;
};
